import * as React from 'react'

import { Alert, Box, Button, Spinner } from '@factorialco/deprecated-design-system'
import { F0Alert, F0Box, F0Dialog, F0Text } from '@factorialco/f0-react'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import i18n from 'modules/core/lib/i18n'

import { useDialogPortalContainer } from 'modules/documents/components/DocumentPreview/useDialogPortalContainer'

import type { EsignatureRequest } from 'modules/esignatures/hooks/requests/useGetEsignatureRequests'

interface Props {
  saving: boolean
  disabledButton: boolean
  onSubmit: () => void
  onClose: () => void
  signatureRequests: EsignatureRequest[]
  useF0?: boolean
}

const FillableModal = ({
  saving,
  disabledButton,
  onSubmit,
  onClose,
  signatureRequests,
  useF0 = false,
}: Props) => {
  const portalContainer = useDialogPortalContainer()
  const actionsDisabled = saving || disabledButton
  const description = saving
    ? i18n.t('documents.modal.fillable.modal.loading')
    : i18n.t('documents.modal.fillable.modal.description')

  if (useF0) {
    return (
      <F0Dialog
        isOpen
        width="sm"
        title={i18n.t('documents.modal.fillable.modal.title')}
        onClose={actionsDisabled ? () => null : onClose}
        container={portalContainer}
        primaryAction={{
          label: i18n.t('documents.modal.fillable.modal.confirm'),
          onClick: onSubmit,
          disabled: actionsDisabled,
          loading: disabledButton,
        }}
        secondaryAction={{
          label: i18n.t('documents.modal.fillable.modal.back'),
          onClick: onClose,
          disabled: disabledButton,
        }}
      >
        <F0Box display="flex" flexDirection="column" gap="xl">
          <F0Text variant="body" content={description} />
          {!saving && signatureRequests.length > 0 && (
            <F0Alert
              variant="warning"
              title={i18n.t('documents.modal.fillable.modal.esignatures_description')}
              description=""
            />
          )}
        </F0Box>
      </F0Dialog>
    )
  }

  return (
    <Modal
      locked={actionsDisabled}
      illustration="alert"
      title={i18n.t('documents.modal.fillable.modal.title')}
      description={description}
      onClose={actionsDisabled ? () => null : onClose}
      onSubmit={actionsDisabled ? () => null : onSubmit}
      footer={
        <>
          <Button type="secondary" onClick={onClose} disabled={disabledButton}>
            {i18n.t('documents.modal.fillable.modal.back')}
          </Button>
          <Button submit disabled={actionsDisabled} loading={disabledButton}>
            {i18n.t('documents.modal.fillable.modal.confirm')}
          </Button>
        </>
      }
    >
      {saving && (
        <Box padding="s12" width="full" alignItems="center">
          <Spinner />
        </Box>
      )}
      {!saving && signatureRequests.length > 0 && (
        <Alert color="warning">
          {i18n.t('documents.modal.fillable.modal.esignatures_description')}
        </Alert>
      )}
    </Modal>
  )
}

export default observer(FillableModal)
